import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react'
import { Divider, Menu, Classes } from '@blueprintjs/core'
import { Atom, useAtom } from 'klyva'
import { chunk, isEqual } from 'lodash'
import styled from 'styled-components'

import Box from '../box'
import { default as Switch } from '../menu-item-switch'
import RadioDropdown from '../radio-dropdown'
import { BaseColumnEntry, BulkTogglesConfig, OrderOptionsConfig } from './types'
import getReorder from './get-reorder'

const SwitchColumn = styled(Menu)`
  flex: 6;
  height: fit-content;
  && {
    background-color: transparent;
  }
  li {
    max-height: 30px;
  }
`

const TogglesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 450px;
  box-sizing: border-box;
  overflow: auto;
  margin-top: 10px;
  > div {
    display: flex;
    flex-wrap: wrap;
    height: 100%;
    min-height: min-content;
    max-width: 420px;
    overflow: auto;
    padding-bottom: 20px;
    > ul {
      padding-bottom: 0;
      padding-top: 0;
    }
  }
  ${`.${Classes.SWITCH}`} {
    max-width: 100%;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`

type SelectColumnsProps<
  T extends string,
  U extends BaseColumnEntry<T>,
  BulkToggleKey extends string,
  OrderOptionKey extends string,
> = PropsWithChildren<{
  columns: U[]
  bulkTogglesConfig: BulkTogglesConfig<T, U, BulkToggleKey>
  enabledColumnIdsAtom: Atom<T[]>
  numColumnsForToggles: number
  orderOptionsConfig: OrderOptionsConfig<T, OrderOptionKey>
  currentOrderOption: OrderOptionKey | 'custom'
  togglingDisabledColumns: Set<T>
}>

function ToggleColumnsPane<
  T extends string,
  U extends BaseColumnEntry<T>,
  BulkToggleKey extends string,
  OrderOptionKey extends string,
>({
  columns,
  bulkTogglesConfig,
  enabledColumnIdsAtom,
  numColumnsForToggles,
  orderOptionsConfig,
  currentOrderOption,
  togglingDisabledColumns,
}: SelectColumnsProps<T, U, BulkToggleKey, OrderOptionKey>) {
  const [enabledColumnIds, setEnabledColumnIds] = useAtom(enabledColumnIdsAtom)
  const [restorableOrderedIds, setRestorableOrderedIds] = useState<
    undefined | T[]
  >(undefined)

  const chunkedColumnIds = chunk(
    columns.map(({ id }) => id),
    Math.ceil(columns.length / Math.max(numColumnsForToggles, 1)),
  )
  const reorder = useMemo(
    () => getReorder(orderOptionsConfig, currentOrderOption),
    [orderOptionsConfig, currentOrderOption],
  )
  const [selectedBulkToggleOption, setSelectedBulkToggleOption] = useState<
    BulkToggleKey | undefined
  >(undefined)

  const getNextEnabledIdOrdering = useCallback(
    (nextBulkToggleKey: BulkToggleKey | undefined): T[] => {
      const orderedIdsToRestore = restorableOrderedIds
        ? [...restorableOrderedIds]
        : undefined

      // determine which columns should now be enabled based on nextBulkToggleKey
      const nextEnabledIds = columns.flatMap((column) => {
        if (
          nextBulkToggleKey === undefined &&
          selectedBulkToggleOption === undefined
        ) {
          return [...enabledColumnIds]
        }
        const { id } = column
        const enabled = enabledColumnIds.includes(id)
        const desiredEnabled = nextBulkToggleKey
          ? bulkTogglesConfig[nextBulkToggleKey].getEntryDesiredEnabled(
              column,
              enabledColumnIds,
            )
          : orderedIdsToRestore?.includes(id as T) // Note: immer types this as Draft<T>, but it's not a real issue
        if (typeof desiredEnabled === 'boolean') {
          return desiredEnabled ? [id] : []
        } else {
          return enabled ? [id] : []
        }
      })
      // determine the ordering of the columns
      const nextEnabledIdOrdering = reorder(
        enabledColumnIds
          .filter((id) => nextEnabledIds.includes(id)) // keep previously enabled ids in the same order as before
          .concat(
            // add newly enabled ids at the end
            nextEnabledIds.filter((id) => !enabledColumnIds.includes(id)),
          ),
      )
      return nextEnabledIdOrdering
    },
    [
      bulkTogglesConfig,
      enabledColumnIds,
      columns,
      restorableOrderedIds,
      selectedBulkToggleOption,
      reorder,
    ],
  )

  const bulkToggleKeys = Object.keys(bulkTogglesConfig) as BulkToggleKey[]
  useEffect(() => {
    if (
      selectedBulkToggleOption &&
      isEqual(
        getNextEnabledIdOrdering(selectedBulkToggleOption),
        enabledColumnIds,
      )
    ) {
      // return early if the selection matches the bulk toggle
      return
    }
    // If the user toggles entries in such a way that the selected bulk toggle option is no
    //   longer correct, detect the appropriate option
    for (const bulkToggleKey of bulkToggleKeys) {
      if (
        bulkToggleKey !== selectedBulkToggleOption &&
        isEqual(getNextEnabledIdOrdering(bulkToggleKey), enabledColumnIds)
      ) {
        setSelectedBulkToggleOption(bulkToggleKey)
        return
      }
    }
    setSelectedBulkToggleOption(undefined)
  }, [
    selectedBulkToggleOption,
    setSelectedBulkToggleOption,
    bulkToggleKeys,
    getNextEnabledIdOrdering,
    enabledColumnIds,
    restorableOrderedIds,
  ])

  // handle bulk toggle changes
  const updateSelectedBulkToggleKey = (
    nextBulkToggleKey: BulkToggleKey | undefined,
  ) => {
    const nextEnabledIdOrdering = getNextEnabledIdOrdering(nextBulkToggleKey)
    setSelectedBulkToggleOption(nextBulkToggleKey)
    if (isEqual(nextEnabledIdOrdering, enabledColumnIds)) {
      return
    }
    if (nextBulkToggleKey === undefined) {
      // turned off the bulk toggle that was just set
      //  so we need to restore the previous toggle settings
      setRestorableOrderedIds(undefined)
    } else if (selectedBulkToggleOption === undefined) {
      // previously a bulk toggle option did not apply
      //  but now one does, so save current ordering
      //  so it can be restored if the user switches back to custom
      setRestorableOrderedIds(enabledColumnIds)
    }
    setEnabledColumnIds(nextEnabledIdOrdering)
  }
  return (
    <Box width="1">
      <Box flexDir="row" mb={2}>
        <RadioDropdown
          tooltipContent="Click to see alternative selection shortcuts"
          options={bulkToggleKeys}
          selectedOption={selectedBulkToggleOption}
          setSelectedOption={(bulkToggleKey) => {
            updateSelectedBulkToggleKey(bulkToggleKey)
          }}
          getOptionText={(bulkToggleKey, selected) => {
            const config = bulkToggleKey
              ? bulkTogglesConfig[bulkToggleKey]
              : undefined
            if (!config) {
              return selected
                ? 'Showing Custom Selection'
                : 'Show Custom Selection'
            } else {
              return (
                (selected ? config.selectedLabel : undefined) ?? config.label
              )
            }
          }}
          getDisabled={(bulkToggleKey) => {
            const subsequentEnabledIdOrdering =
              getNextEnabledIdOrdering(bulkToggleKey)
            return isEqual(subsequentEnabledIdOrdering, enabledColumnIds)
          }}
        />
      </Box>
      <Divider />
      <TogglesWrapper>
        <div>
          {chunkedColumnIds.map((someColumnIds, columnIndex) => (
            <SwitchColumn key={columnIndex}>
              {someColumnIds.map((columnId) => {
                const column = columns.find(({ id }) => id === columnId)
                if (!column) {
                  return null // just for TS
                }
                const currentlyEnabled = enabledColumnIds.includes(columnId)
                const toggleEntry = () => {
                  setEnabledColumnIds(
                    reorder(
                      currentlyEnabled
                        ? enabledColumnIds.filter((id) => id !== columnId)
                        : enabledColumnIds.concat(columnId),
                    ),
                  )
                }
                // prevent disabling a column if it is the only enabled column
                const togglingDisabled =
                  isEqual(enabledColumnIds, [columnId]) ||
                  togglingDisabledColumns.has(columnId)
                return (
                  <Switch
                    key={columnId}
                    disabled={togglingDisabled}
                    onClick={() => {
                      if (!togglingDisabled) {
                        toggleEntry()
                      }
                    }}
                    checked={currentlyEnabled}
                    label={column.label}
                  />
                )
              })}
            </SwitchColumn>
          ))}
        </div>
      </TogglesWrapper>
    </Box>
  )
}

export default ToggleColumnsPane
